import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Moment from 'moment';
import { Popper } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  blackColor,
  dangerColor,
  hexToRgb,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import DATA_RANGE_FORMAT from 'constants/dataRangeFormat';
import { post } from 'helpers/apiHelpers';
import Button from 'components/CustomButtons/Button';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import DateRange from 'components/DateRange/DateRange';

const BAGS_DATA_DEFAULT_STATE = {
  firstDateRange: 0,
  secondDateRange: 0,
};

const useStyles = makeStyles({
  popper: {
    overflow: 'visible',
    zIndex: '2',
    backgroundColor: whiteColor,
    boxShadow: `0 10px 50px 0 rgba(${hexToRgb(blackColor)}, 0.2)`,
  },
  content: {
    minWidth: '295px',
    padding: '16px',
  },
  labeledCheckbox: {
    marginBottom: '4px',
  },
  secondRange: {
    marginTop: '16px',
  },
  error: {
    margin: '12px 0 0',
    fontSize: '14px',
    color: dangerColor[0],
  },
  computeButton: {
    margin: '16px 0 0',
  },
});

const MultiplierPopper = ({
  popperContentRef,
  anchorEl,
  isOpen,
  onMultiplierChange,
  matrix,
}) => {
  const classes = useStyles();

  const { t } = useTranslation();

  const [isDataRangeActive, setIsDataRangeActive] = useState(false);

  const [range, setRange] = useState([undefined, undefined]);

  const [firstRange, setFirstRange] = useState([undefined, undefined]);
  const [secondRange, setSecondRange] = useState([undefined, undefined]);

  const [bagsData, setBagsData] = useState(BAGS_DATA_DEFAULT_STATE);

  const [rangeFrom, rangeTo] = range;
  const [firstRangeFrom, firstRangeTo] = firstRange;
  const [secondRangeFrom, secondRangeTo] = secondRange;

  const isRangeFilled = !!(rangeFrom && rangeTo);
  const isFirstRangeFilled = !!(firstRangeFrom && firstRangeTo);
  const isSecondRangeFilled = !!(secondRangeFrom && secondRangeTo);

  const getDateRangeErrorMessage = ([dateFrom, dateTo]) => {
    if (dateFrom && dateTo) {
      // eslint-disable-next-line new-cap -- FIXME
      const dateFromMomentObj = Moment(dateFrom, DATA_RANGE_FORMAT);
      // eslint-disable-next-line new-cap -- FIXME
      const dateToMomentObj = Moment(dateTo, DATA_RANGE_FORMAT);

      if (!dateFromMomentObj.isBefore(dateToMomentObj)) {
        return t('reports.dateFromLowerThanDateToError');
      }
    }

    return '';
  };

  const getDataRangesErrorMessage = () => {
    if (isDataRangeActive && isFirstRangeFilled && isSecondRangeFilled) {
      // eslint-disable-next-line new-cap -- FIXME
      const firstRangeFromMomentObj = Moment(firstRangeFrom, DATA_RANGE_FORMAT);
      // eslint-disable-next-line new-cap -- FIXME
      const firstRangeToMomentObj = Moment(firstRangeTo, DATA_RANGE_FORMAT);
      // eslint-disable-next-line new-cap -- FIXME
      const secondRangeFromMomentObj = Moment(
        secondRangeFrom,
        DATA_RANGE_FORMAT
      );
      // eslint-disable-next-line new-cap -- FIXME
      const secondRangeToMomentObj = Moment(secondRangeTo, DATA_RANGE_FORMAT);

      const firstRangeDaysDiff = firstRangeFromMomentObj.diff(
        firstRangeToMomentObj,
        'days'
      );
      const secondRangeDaysDiff = secondRangeFromMomentObj.diff(
        secondRangeToMomentObj,
        'days'
      );

      if (firstRangeDaysDiff !== secondRangeDaysDiff) {
        return t('reports.dateRangeEqualityError');
      }
    }

    return '';
  };

  const dateRangeErrorMessage = getDateRangeErrorMessage(range);
  const firstDateRangeErrorMessage = getDateRangeErrorMessage(firstRange);
  const secondDateRangeErrorMessage = isDataRangeActive
    ? getDateRangeErrorMessage(secondRange)
    : '';
  const dataRangesErrorMessage = getDataRangesErrorMessage();

  const resetBagsData = () => {
    setBagsData(BAGS_DATA_DEFAULT_STATE);
  };

  const onFirstRangeChange = newDateRange => {
    setFirstRange(newDateRange);
    setBagsData(BAGS_DATA_DEFAULT_STATE);
  };

  return (
    <Popper
      className={classes.popper}
      open={isOpen}
      anchorEl={anchorEl}
      disablePortal={true}
      placement="bottom"
      modifiers={{
        flip: {
          enabled: false,
        },
        offset: {
          enabled: true,
          offset: '0 15px',
        },
        preventOverflow: {
          enabled: false,
        },
      }}
    >
      <div className={classes.content} ref={popperContentRef}>
        <LabeledCheckbox
          className={classes.labeledCheckbox}
          label={t('reports.computeBasedOnDateRanges')}
          checked={isDataRangeActive}
          onChange={e => {
            setIsDataRangeActive(e.target.checked);
            resetBagsData();
          }}
        />
        {isDataRangeActive ? (
          <>
            <DateRange
              title={`${t('reports.rangeI')} ${t('reports.rangeBags', {
                bags: bagsData.firstDateRange,
              })}`}
              dataRange={firstRange}
              onDateRangeChange={onFirstRangeChange}
              errorMessage={firstDateRangeErrorMessage}
              labelFrom={t('reports.dateFrom')}
              labelTo={t('reports.dateTo')}
            />
            <DateRange
              className={classes.secondRange}
              title={`${t('reports.rangeII')} ${t('reports.rangeBags', {
                bags: bagsData.secondDateRange,
              })}`}
              dataRange={secondRange}
              onDateRangeChange={newDateRange => {
                setSecondRange(newDateRange);
                resetBagsData();
              }}
              errorMessage={secondDateRangeErrorMessage}
              labelFrom={t('reports.dateFrom')}
              labelTo={t('reports.dateTo')}
            />
            {dataRangesErrorMessage && (
              <p className={classes.error}>{dataRangesErrorMessage}</p>
            )}
          </>
        ) : (
          <DateRange
            dataRange={range}
            onDateRangeChange={newDateRange => {
              setRange(newDateRange);
              resetBagsData();
            }}
            errorMessage={dateRangeErrorMessage}
            labelFrom={`${t('reports.dateI')} ${t('reports.rangeBags', {
              bags: bagsData.firstDateRange,
            })}`}
            labelTo={`${t('reports.dateII')} ${t('reports.rangeBags', {
              bags: bagsData.secondDateRange,
            })}`}
          />
        )}
        <Button
          className={classes.computeButton}
          color="primary"
          fullWidth={true}
          onClick={async () => {
            if (isDataRangeActive) {
              if (
                firstDateRangeErrorMessage ||
                secondDateRangeErrorMessage ||
                dataRangesErrorMessage
              ) {
                return;
              }

              if (!isFirstRangeFilled || !isSecondRangeFilled) {
                toast.error(t('reports.multiplierDate'));
                return;
              }
            } else {
              if (dateRangeErrorMessage) {
                return;
              }

              if (!isRangeFilled) {
                toast.error(t('reports.multiplierDate'));
                return;
              }
            }

            try {
              const ranges = isDataRangeActive
                ? {
                    firstDateFrom: firstRangeFrom,
                    firstDateTo: firstRangeTo,
                    secondDateFrom: secondRangeFrom,
                    secondDateTo: secondRangeTo,
                  }
                : {
                    firstDateFrom: rangeFrom,
                    firstDateTo: rangeFrom,
                    secondDateFrom: rangeTo,
                    secondDateTo: rangeTo,
                  };

              const data = await post('/reports/multiplier_calculator', {
                ...ranges,
                matrix,
              });

              const [{ firstDateRangeBags, secondDateRangeBags, multiplier }] =
                data['hydra:member'];

              onMultiplierChange(multiplier);
              setBagsData({
                firstDateRange: firstDateRangeBags,
                secondDateRange: secondDateRangeBags,
              });
              // eslint-disable-next-line no-empty -- FIXME
            } catch {}
          }}
        >
          {t('reports.compute')}
        </Button>
      </div>
    </Popper>
  );
};

export default MultiplierPopper;
